import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Printer } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import FindOneTimeSheetLog from "@/v2/api/time-sheet/FindOneTimeSheetLog";
import handlePrint from "@/utils/handlePrint";
import TimesheetPDF from "./TimeSheetPdf";
import { format, parseISO } from "date-fns";
import { TimeSheetStages } from "@/v2/types/TimeSheetPrint";
import { TimeSheetStatus } from "@/v2/types/DraftedTimeSheet";

const STAGE_BY_STATUS: Record<TimeSheetStatus, TimeSheetStages> = {
    DRAFTED: "drafted",
    REJECTED: "drafted",
    TO_BE_VERIFIED: "to-be-verified",
    VERIFIED: "verified",
    APPROVED: "approved",
};

function PrintTimeSheet({
    timeSheetId,
    isDraft = false,
    stage = "drafted",
    status,
}: {
    timeSheetId: string;
    isDraft?: boolean;
    stage?: TimeSheetStages;
    /** When given, the printed stage and draft watermark follow the row status. */
    status?: TimeSheetStatus;
}) {
    const resolvedStage = status ? STAGE_BY_STATUS[status] : stage;
    const resolvedIsDraft = status ? status !== "APPROVED" : isDraft;

    const queryClient = useQueryClient();
    const [isPrinting, setIsPrinting] = useState(false);

    const handlePrintProcess = async () => {
        try {
            setIsPrinting(true);

            const data = await queryClient.fetchQuery({
                queryKey: ["time-sheets", timeSheetId],
                queryFn: () => FindOneTimeSheetLog({ timeSheetId }),
            });

            const project = data?.details?.project;
            const supplier = data?.details?.supplier;

            const month = format(
                parseISO(data?.details?.start_date),
                "yyyy-MM",
            );

            await handlePrint(
                <TimesheetPDF
                    month={month}
                    data={data?.logs}
                    hasDeduction={project.deduction}
                    deductionRate={project.deduction_amount}
                    hasOtherDeduction={data?.details?.other_deductions > 0}
                    otherDeductionAmount={data?.details?.other_deductions}
                    otherDeductionReason={data?.details?.deduction_note}
                    comment={data?.details?.note}
                    projectInfo={{
                        poNumber: project.po_number || "",
                        projectDescription: project.description || "",
                        projectLocation: project.location || "",
                        supplierName: supplier.full_name || "",
                    }}
                    isDraft={resolvedIsDraft}
                    stage={resolvedStage}
                />,
                { fileName: `Timesheet-${month}-${supplier.full_name}` },
            );
        } catch (error) {
            console.error("Failed to fetch timesheet:", error);
        } finally {
            setIsPrinting(false);
        }
    };

    return (
        <div>
            <Button
                variant="outline"
                onClick={handlePrintProcess}
                disabled={isPrinting}
                className="size-8"
            >
                {isPrinting ? (
                    <Loader2 className="animate-spin" />
                ) : (
                    <Printer />
                )}
            </Button>
        </div>
    );
}

export default PrintTimeSheet;
